import { useState } from "react";
import { Alert } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { extractErrorMessage } from "@/core/api/errors";
import { useOnboardingNavigation } from "./shared-query";
import {
  usePublishStepPayload,
  type StepPayload,
} from "./step-payload-context";
import type {
  IntakeStep,
  OnboardingAnswerUpdates,
  SimpleBusinessSetupPayload,
} from "./types";

export const StepNavigation = ({
  businessId,
  step,
  answers,
  business,
  disabled = false,
  nextDisabled = false,
  incompletePayload,
}: {
  businessId: string;
  step: IntakeStep;
  /** Saved with Continue, so the step's answers and its position move together. */
  answers?: OnboardingAnswerUpdates;
  /** Business setup fields the step owns, saved with the position. */
  business?: SimpleBusinessSetupPayload;
  disabled?: boolean;
  nextDisabled?: boolean;
  /** Valid partial data to save when leaving a step that cannot continue yet. */
  incompletePayload?: StepPayload;
}) => {
  const mutation = useOnboardingNavigation(businessId);
  const [error, setError] = useState<string>();
  const payload: StepPayload = {
    ...(answers ? { answers } : {}),
    ...(business ? { business } : {}),
  };
  // Leaving by the checklist sends the same thing Continue would, so a finished step is
  // saved and marked either way. Incomplete steps can supply a valid partial payload,
  // such as withdrawing a confirmation, without submitting invalid form fields.
  const leavePayload = nextDisabled ? incompletePayload : payload;
  usePublishStepPayload(leavePayload);
  const go = (direction: "next" | "back") => {
    setError(undefined);
    mutation.mutate(
      {
        step,
        direction,
        ...(direction === "back" ? leavePayload : payload),
      },
      {
        onError: (failure) => setError(extractErrorMessage(failure).message),
      },
    );
  };
  return (
    <Stack className="w-full" gap={2} align="stretch">
      {error && <Alert color="danger">{error}</Alert>}
      <Stack
        className="w-full"
        align="start"
        direction="row"
        gap={2}
        justify="end"
      >
        <Button
          variant="outline"
          onClick={() => go("back")}
          disabled={disabled || mutation.isPending}
        >
          Back
        </Button>
        <Button
          onClick={() => go("next")}
          disabled={disabled || nextDisabled || mutation.isPending}
          aria-busy={mutation.isPending || undefined}
        >
          {mutation.isPending && (
            <Spinner aria-hidden data-icon="inline-start" />
          )}
          Continue
        </Button>
      </Stack>
    </Stack>
  );
};
